import { consola } from 'consola'
import type {
  CalculateOrderInput,
  CalculateOrderResponse,
  CreateOrderInput,
  CreateOrderResponse,
  GetUserOrdersParams,
  GetUserOrdersResponse,
  GetRetryPaymentOrdersResponse,
  Order,
  OrderStatus,
  PaymentStatus,
} from '../model/types'
import { useApi } from '@shared/api'

const logger = consola.withTag('api-order')

/**
 * API-клиент заказов пользователя.
 * Базовый URL: /api/v2/orders
 *
 * SRP: только операции с заказами.
 * Корзина → entities/order/api/apiCart.ts
 * Платежи → features/payment/api/
 */
class APIOrder {
  /**
   * Создать новый заказ.
   * POST /api/v2/orders
   */
  async createOrder(orderData: CreateOrderInput): Promise<CreateOrderResponse> {
    logger.debug('Creating order')
    const response = await useApi(true).post('/orders', { body: orderData })
    logger.success('Order created:', { orderId: response.data?.order?.id })
    return response.data
  }

  /**
   * PREVIEW-расчёт заказа без сохранения.
   * POST /api/v2/orders/calculate
   *
   * Возвращает оба сценария оплаты (баллы / приветственная скидка) и точные суммы
   * (сервисный сбор, итог, начисляемые баллы) из единого источника истины бэкенда.
   */
  async calculateOrder(input: CalculateOrderInput): Promise<CalculateOrderResponse> {
    const response = await useApi(true).post('/orders/calculate', { body: input })
    return response.data
  }

  /**
   * Получить детали заказа по ID.
   * GET /api/v2/orders/{id}
   */
  async getOrderDetails(orderId: string): Promise<Order> {
    const response = await useApi(true).get(`/orders/${orderId}`)

    if (response.data?.success && response.data?.order) {
      return response.data.order
    }

    return response.data
  }

  /**
   * Получить статус заказа (для polling).
   * GET /api/v2/orders/{id}
   */
  async getOrderStatus(orderId: string): Promise<{ status: OrderStatus; paymentStatus: PaymentStatus }> {
    const order = await this.getOrderDetails(orderId)
    return { status: order.status, paymentStatus: order.paymentStatus }
  }

  /**
   * Legacy-совместимость с APIProfile.getOrder: статус + payment_status (snake_case) для виджетов оплаты.
   */
  async getOrder(orderId: string | number): Promise<{ status: string; paymentStatus: string; payment_status: string }> {
    const { status, paymentStatus } = await this.getOrderStatus(String(orderId))
    const ps = String(paymentStatus)
    return { status: String(status), paymentStatus: ps, payment_status: ps }
  }

  /**
   * Получить историю заказов пользователя.
   * GET /api/v2/orders/user/{userId}
   */
  async getOrderHistory(params: GetUserOrdersParams): Promise<GetUserOrdersResponse> {
    const response = await useApi(true).get(`/orders/user/${params.userId}`, {
      params: {
        limit: params.limit,
        offset: params.offset,
        status: params.status,
        orderBy: params.orderBy,
      },
    })
    return response.data
  }

  /**
   * Получить заказы для повторной оплаты.
   * GET /api/v2/orders/user/{userId}/retry-payment
   */
  async retryPayment(userId: string): Promise<GetRetryPaymentOrdersResponse> {
    logger.debug('Fetching retry-payment orders for user:', userId)
    const response = await useApi(true).get(`/orders/user/${userId}/retry-payment`)
    logger.success('Retry-payment orders fetched:', { count: response.data?.orders?.length ?? 0 })
    return response.data
  }

  /**
   * Отправить оценку заказа.
   * POST /api/v2/orders/{orderId}/rating
   *
   * userId берётся на бэкенде из токена авторизации, поэтому в теле запроса
   * не передаётся (DTO с forbidNonWhitelisted отклонит лишние поля).
   */
  async sendOrderRating(params: {
    orderId: number
    userId?: number
    quality: number
    comment?: string
  }): Promise<any> {
    const { orderId, quality, comment } = params
    const body: Record<string, any> = { quality }
    if (comment) body.comment = comment

    const response = await useApi(true).post(`/orders/${orderId}/rating`, { body })
    return response.data
  }
}

export const apiOrder = new APIOrder()
export default apiOrder
